/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { EmployeeTipsDashboard } from './components/EmployeeTipsDashboard';
import { ApprovalsPage } from './components/ApprovalsPage';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { TermsOfService } from './components/TermsOfService';
import { SaaSAgreement } from './components/SaaSAgreement';
import { BrowserRouter, Routes, Route, Link, Navigate } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Dashboard } from './components/Dashboard';
import { SuperAdminPortal } from './components/SuperAdminPortal';
import { UserManagementDashboard } from './components/UserManagementDashboard';
import { JarTipsPage } from './components/JarTipsPage';
import { PersonalCashTipsPage } from './components/PersonalCashTipsPage';
import { DisputesTabs } from './components/DisputesTabs';
import { SettingsTabs } from './components/SettingsTabs';
import { StaffRosterPage } from './components/StaffRosterPage';
import { CommunicationHub } from './components/CommunicationHub';
import { AllocationsDashboard } from './components/AllocationsDashboard';
import { TipAnalysisDashboard } from './components/TipAnalysisDashboard';
import { BenchmarkTools } from './components/BenchmarkTools';
import { AuditPackageButton } from './components/AuditPackageButton';
import { PayrollExportForm } from './components/PayrollExportForm';
import { TransactionAuditViewer } from './components/TransactionAuditViewer';
import { POSImportDashboard } from './components/POSImportDashboard';
import { ReviewApproveTips } from './components/ReviewApproveTips';
import { ApprovalButton } from './components/ApprovalButton';

import { LandingPage } from './components/LandingPage';
import { useFirebase } from './contexts/FirebaseContext';
import { auth } from './firebase';
import { UserSetupSelection } from './components/UserSetupSelection';
import { UserSetupForm } from './components/UserSetupForm';
import { OnboardingForm } from './components/OnboardingForm';
import { DebugOverlay } from './components/DebugOverlay';
import { SubscriptionGate } from './components/SubscriptionGate';
import { PromoterPortal } from './components/PromoterPortal';
import { GrossReceiptsDashboard } from './components/GrossReceiptsDashboard';
import { UserGuide } from './components/UserGuide';
import { ErrorBoundary } from './components/ErrorBoundary';
import { LocationProvider } from './contexts/LocationContext';

export default function App() {
  const { user, tenantId, role, loading } = useFirebase();
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@growezy.ai';

  if (loading) return <div>Loading...</div>;

  return (
    <ErrorBoundary>
      <LocationProvider>
        <BrowserRouter>
          <DebugOverlay />
          <Routes>
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/saas-agreement" element={<SaaSAgreement />} />
            <Route path="/promoter-portal" element={<PromoterPortal />} />
            <Route path="/onboarding" element={!user ? <Navigate to="/" /> : tenantId ? <Navigate to="/" replace /> : <OnboardingForm />} />
            <Route path="/setup" element={!user ? <Navigate to="/" /> : <UserSetupSelection />} />
            <Route path="/setup-link" element={!user ? <Navigate to="/" /> : <UserSetupForm />} />
        
        <Route path="/" element={
          !user ? (
            <LandingPage />
          ) : (!tenantId && !isSuperAdmin) ? (
            sessionStorage.getItem('pendingInvite') ? <UserSetupForm /> : <OnboardingForm />
          ) : (
            <Layout />
          )
        }>
          {/* Nested routes for Layout */}
          <Route index element={
            role === 'admin' || role === 'manager' || isSuperAdmin ? (
              <Dashboard />
            ) : (
              <EmployeeTipsDashboard />
            )
          } />
          <Route path="tips" element={
            <PersonalCashTipsPage />
          } />
          <Route path="jar-tips" element={
            <JarTipsPage />
          } />
          <Route path="personal" element={
            <EmployeeTipsDashboard />
          } />
          <Route path="approvals" element={
            <ApprovalsPage />
          } />
          <Route path="disputes" element={
            <DisputesTabs tenantId={tenantId!} isAdminOrManager={role === 'admin' || role === 'manager' || isSuperAdmin} />
          } />
          <Route path="communication" element={
            <CommunicationHub tenantId={tenantId!} />
          } />
          {(role === 'admin' || role === 'manager' || isSuperAdmin) && (
            <Route path="allocations" element={
              <AllocationsDashboard tenantId={tenantId!} />
            } />
          )}
          {(role === 'admin' || role === 'manager' || isSuperAdmin) && (
            <Route path="payroll" element={
              <div className="space-y-8">
                <PayrollExportForm tenantId={tenantId!} />
              </div>
            } />
          )}
          {(role === 'admin' || role === 'manager' || isSuperAdmin) && (
            <Route path="staff-roster" element={<StaffRosterPage />} />
          )}
          {(role === 'admin' || role === 'owner' || isSuperAdmin) && (
            <>
              <Route path="settings" element={<SettingsTabs tenantId={tenantId!} />} />
              <Route path="settings/:tab" element={<SettingsTabs tenantId={tenantId!} />} />
            </>
          )}
          <Route path="benchmarks" element={
            <SubscriptionGate featureName="Benchmarks" featureId="benchmarks">
              <div className="space-y-8">
                <BenchmarkTools />
              </div>
            </SubscriptionGate>
          } />
          {(role === 'admin' || role === 'manager' || isSuperAdmin) && (
            <>
              <Route path="pos-import" element={<POSImportDashboard tenantId={tenantId!} />} />
              <Route path="pos-history" element={<Navigate to="/pos-import" replace />} />
              <Route path="review-approve" element={<ReviewApproveTips tenantId={tenantId!} />} />
              <Route path="review-tips" element={<Navigate to="/review-approve" replace />} />
              <Route path="reports" element={<TransactionAuditViewer tenantId={tenantId!} />} />
              <Route path="user-management" element={<UserManagementDashboard />} />
              <Route path="shortfall" element={<GrossReceiptsDashboard tenantId={tenantId!} />} />
            </>
          )}
          <Route path="guide" element={<UserGuide />} />
          {isSuperAdmin && (
            <Route path="super-admin-portal" element={<SuperAdminPortal tenantId={tenantId!} />} />
          )}
        </Route>
        {/* Catch-all redirect for unmatched routes or unauthenticated visitors */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
      </LocationProvider>
    </ErrorBoundary>
  );
}
